import { gql, useMutation, useQuery } from '@apollo/client';
import type { Exchanges } from '@interfaces/financialProduct';

import {
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  InputAdornment,
  styled,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material';
import { dollars2cents } from '@utils/dollars2cents';
import { isCurrencySymbolAfter } from '@utils/moneyFormatting';
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  Typography,
  useSnackbar,
} from 'hephaestus/web';
import { BackupTable as BackupTableIcon } from 'hephaestus/webIcons';
import { round } from 'lodash-es';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

const DISCREPANCY_THRESHOLD_PCT_PRICE = 3;
const DISCREPANCY_THRESHOLD_PCT_QUANTITY = 0;

const FETCH_BULK_TRADE_RUN = gql`
  query fetchBulkTradeRun(
    $bulkTradeRunId: ObjectID!
    $exchanges: [Exchanges!]
  ) {
    fetchBulkTradeRun(bulkTradeRunId: $bulkTradeRunId) {
      bulkTradeRun {
        id
        bulkTradeRequests(exchanges: $exchanges) {
          type
          bulkTrades {
            _id
            expectedTradePriceCents
            expectedTradePriceInCurrencyCents
            currency
            total
            actualTotal
            actualTradePriceCents
            actualTradePriceInCurrencyCents
            financialProduct {
              id
            }
          }
        }
      }
    }
  }
`;

export type AccountBulkTrade = {
  _id: string;
  expectedTradePriceCents: number;
  expectedTradePriceInCurrencyCents: number;
  expectedTradePriceInCurrency: number;
  currency: string;
  total: number;
  originalPriceInCurrencyCents: number;
  originalPriceInCurrency: number;
  originalTotal: number;
  actualTradePriceCents?: number;
  actualTradePriceInCurrencyCents?: number;
  actualTotal?: number;
};

const UPDATE_PRICE_AND_QUANTITY = gql`
  mutation calculateTradeRequestFills(
    $input: CalculateTradeRequestFillsInput!
  ) {
    calculateTradeRequestFills(input: $input) {
      success
    }
  }
`;

export const limitDecimals = (number: string): string => {
  let value = number.toString();

  const [integerPart, decimalPart] = value.split('.');

  if (decimalPart) {
    const limitedDecimal = decimalPart.slice(0, 6);
    value = `${integerPart}.${limitedDecimal}`;
  }

  return value; // should be a string to allow for a decimal point, converted to a number later
};

export const bulkPriceUpdate = (
  bulkTrades: any[],
  content: string,
  priceField: string,
  callback: (
    bulkTrades: any[],
    isHasErrors: boolean,
    notFoundSecurities: string[],
  ) => void,
) => {
  const mappedValues = content.split('\n').map((x: any) => x.split('\t'));
  let isHasErrors = false;
  const notFoundSecurities: string[] = [];
  const localBulkTrades: any[] = [...bulkTrades];

  mappedValues.forEach((line: any) => {
    if (line[0]) {
      const index = bulkTrades.findIndex((x: any) => x._id === line[0]);
      const price = round(parseFloat((line[1] ?? '').replace('$', '')), 6);
      const quantity = parseInt(line[2] ?? '', 10);

      if (index > -1) {
        if (!Number.isNaN(price)) {
          localBulkTrades[index][priceField] = price;
        }

        if (!Number.isNaN(quantity)) {
          localBulkTrades[index].total = quantity;
        }
      } else {
        notFoundSecurities.push(line[0]);
      }
    }
  });

  if (notFoundSecurities.length) {
    isHasErrors = true;
  }

  callback(localBulkTrades, isHasErrors, notFoundSecurities);
};

const UpdateTradesTable = ({
  type,
  bulkTradeRunId,
  setDiscrepancyWarning,
  exchanges = [],
  onPriceUpdate,
}: {
  type: 'BUY' | 'SELL';
  bulkTradeRunId: string;
  setDiscrepancyWarning: any;
  exchanges?: Exchanges[];
  onPriceUpdate?: () => void;
}) => {
  const { t } = useTranslation('bulkTrader');
  const { enqueueSnackbar } = useSnackbar();
  const symbolAfter = isCurrencySymbolAfter();

  const [bulkTrades, setBulkTrades] = useState<AccountBulkTrade[]>([]);
  const [open, setOpen] = useState(false);
  const [content, setContent] = useState('');

  const { data, loading } = useQuery(FETCH_BULK_TRADE_RUN, {
    variables: {
      bulkTradeRunId,
      ...(exchanges.length > 0 && { exchanges }),
    },
    fetchPolicy: 'no-cache',
  });

  const [updatePriceAndQuantity, { loading: updateLoading }] = useMutation(
    UPDATE_PRICE_AND_QUANTITY,
    {
      onCompleted: () => {
        if (onPriceUpdate) {
          onPriceUpdate();
        }
      },
    },
  );

  useEffect(() => {
    if (data) {
      const request =
        data.fetchBulkTradeRun.bulkTradeRun.bulkTradeRequests.find(
          (x: { type: string; bulkTrades: AccountBulkTrade[] }) =>
            x.type === type,
        );

      if (request?.bulkTrades) {
        setBulkTrades(
          request.bulkTrades.map((bt: AccountBulkTrade) => {
            const expectedTradePriceInCurrencyCents =
              bt.actualTradePriceInCurrencyCents ??
              bt.actualTradePriceCents ??
              bt.expectedTradePriceInCurrencyCents ??
              bt.expectedTradePriceCents;
            const expectedTradePriceInCurrency = round(
              (expectedTradePriceInCurrencyCents ?? 0) / 100,
              6,
            );

            return {
              ...bt,
              originalPriceCents: bt.expectedTradePriceCents,
              originalPriceInCurrencyCents:
                bt.expectedTradePriceInCurrencyCents,
              originalPriceInCurrency: round(
                (bt.expectedTradePriceInCurrencyCents ?? 0) / 100,
                6,
              ),
              originalTotal: bt.total,
              total: bt.actualTotal ?? bt.total,
              expectedTradePriceInCurrencyCents,
              expectedTradePriceInCurrency,
              currency: bt.currency || 'CAD',
            };
          }),
        );
      }
    }
  }, [data, type]);

  const bulkUpdate = () => {
    bulkPriceUpdate(
      bulkTrades,
      content,
      'expectedTradePriceInCurrency',
      (
        updatedBulkTrades: any[],
        isHasErrors: boolean,
        notFoundSecurities: string[],
      ) => {
        if (isHasErrors) {
          enqueueSnackbar(
            t('securitiesWereNotFound', {
              securities: notFoundSecurities.join(', '),
            }),
            { variant: 'warning' },
          );
        }

        if (!isHasErrors) {
          setBulkTrades(updatedBulkTrades);

          setOpen(false);
        }
      },
    );
  };

  const percentualDifference = (a: number, b: number): number => {
    try {
      return Math.abs(100 * ((a - b) / b));
    } catch {
      return 0;
    }
  };

  useEffect(() => {
    let numWarnings = 0;
    bulkTrades.forEach((bt) => {
      if (
        percentualDifference(
          bt.expectedTradePriceInCurrency,
          bt.originalPriceInCurrency,
        ) > DISCREPANCY_THRESHOLD_PCT_PRICE
      ) {
        numWarnings++;
      }
      if (
        percentualDifference(bt.total, bt.originalTotal) >
        DISCREPANCY_THRESHOLD_PCT_QUANTITY
      ) {
        numWarnings++;
      }
    });
    setDiscrepancyWarning(numWarnings > 0);
  }, [bulkTrades, setDiscrepancyWarning]);

  const DifferenceChip = ({
    newValue,
    origValue,
    warningThresholdPct = 0,
  }: {
    newValue: number;
    origValue: number;
    warningThresholdPct?: number;
  }): JSX.Element => {
    const delta = percentualDifference(newValue, origValue);
    const text = `${(newValue > origValue ? '+' : '-') + delta.toFixed(2)}%`;
    return (
      <Chip
        label={text}
        color={delta >= warningThresholdPct ? 'warning' : 'default'}
        sx={{ marginLeft: '0.5em' }}
      />
    );
  };

  const NarrowTableCell = styled(TableCell)({ padding: 0, align: 'right' });

  /**
   * use regex to prevent non numeric values
   * avoiding using "type='number'" on an input
   * @see https://mui.com/material-ui/react-text-field/#type-quot-number-quot
   */
  const updateTradePrice = (value: string, index: number): void => {
    if (!/^(\d+(\.\d*)?|)$/.test(value)) return;

    bulkTrades[index].expectedTradePriceInCurrency = limitDecimals(
      value,
    ) as any;
    setBulkTrades([...bulkTrades]);
  };

  const sanitizeTradePrice = (value: string, index: number): void => {
    bulkTrades[index].expectedTradePriceInCurrency =
      (Number(
        value.toString().replace(/^0+|(?:\.|(\..*?))0+$/gm, '$1'),
      ) as any) || 0;
    setBulkTrades([...bulkTrades]);
  };

  return (
    <Box data-testid={`bulk-trader-update-trades-table-${type.toLowerCase()}`}>
      {loading ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          data-testid={`bulk-trader-update-trades-loading-${type.toLowerCase()}`}
        >
          <CircularProgress sx={{ m: 18 }} />
        </Box>
      ) : !bulkTrades.length ? (
        <></>
      ) : (
        <>
          <Grid
            container
            sx={{
              justifyContent: 'space-between',
            }}
          >
            <Grid>
              <Typography
                sx={{ p: 2 }}
                variant='headingSmall'
                data-testid={`bulk-trader-update-trades-title-${type.toLowerCase()}`}
              >
                {t(`bulk${type}Trades`)}
              </Typography>
            </Grid>
            <Grid>
              <IconButton
                onClick={() => setOpen(true)}
                sx={{ m: 2 }}
                data-testid={`bulk-trader-update-trades-bulk-import-${type.toLowerCase()}`}
                aria-label='bulk import'
              >
                <BackupTableIcon fontSize='inherit' />
              </IconButton>
            </Grid>
          </Grid>
          <Table
            data-testid={`bulk-trader-update-trades-table-${type.toLowerCase()}`}
          >
            <TableHead>
              <TableRow>
                <TableCell
                  data-testid={`bulk-trader-update-trades-header-security-${type.toLowerCase()}`}
                >
                  <Typography variant='labelSmall'>{t('security')}</Typography>
                </TableCell>
                <TableCell
                  data-testid={`bulk-trader-update-trades-header-original-price-${type.toLowerCase()}`}
                ></TableCell>
                <TableCell
                  data-testid={`bulk-trader-update-trades-header-price-${type.toLowerCase()}`}
                >
                  <Typography variant='labelSmall'>{t('price')}</Typography>
                </TableCell>
                <TableCell
                  data-testid={`bulk-trader-update-trades-header-original-quantity-${type.toLowerCase()}`}
                ></TableCell>
                <TableCell
                  data-testid={`bulk-trader-update-trades-header-quantity-${type.toLowerCase()}`}
                >
                  <Typography variant='labelSmall'>{t('quantity')}</Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {bulkTrades.map((line: any, index: number) => (
                <TableRow
                  key={line._id}
                  data-testid={`bulk-trader-update-trades-row-${type.toLowerCase()}-${index}`}
                >
                  <TableCell
                    data-testid={`bulk-trader-update-trades-security-${type.toLowerCase()}-${index}`}
                  >
                    <Typography
                      variant='bodyLarge'
                      sx={{
                        fontWeight: 'bold',
                      }}
                    >
                      {line._id}
                    </Typography>
                  </TableCell>
                  <NarrowTableCell
                    data-testid={`bulk-trader-update-trades-original-price-${type.toLowerCase()}-${index}`}
                  >
                    {line.expectedTradePriceInCurrency !==
                      line.originalPriceInCurrency && (
                      <Typography variant='bodyLarge' color='GrayText'>
                        {Number(line.originalPriceInCurrency).toFixed(2)}
                      </Typography>
                    )}
                  </NarrowTableCell>
                  <TableCell
                    sx={{ display: 'flex', alignItems: 'center' }}
                    data-testid={`bulk-trader-update-trades-price-cell-${type.toLowerCase()}-${index}`}
                  >
                    <TextField
                      value={line.expectedTradePriceInCurrency}
                      onBlur={(e: any) =>
                        sanitizeTradePrice(e.target.value, index)
                      }
                      sx={{ width: '12em' }}
                      onChange={(e: any) =>
                        updateTradePrice(e.target.value, index)
                      }
                      size='small'
                      autoComplete='off'
                      inputMode='decimal'
                      data-testid={`bulk-trader-update-trades-price-input-${type.toLowerCase()}-${index}`}
                      InputProps={{
                        [symbolAfter ? 'endAdornment' : 'startAdornment']: (
                          <InputAdornment
                            position={symbolAfter ? 'end' : 'start'}
                          >
                            {line.currency === 'USD' ? 'US$' : '$'}
                          </InputAdornment>
                        ),
                        inputProps: { min: 0, step: 'any' },
                      }}
                    />
                    {line.expectedTradePriceInCurrency !==
                      line.originalPriceInCurrency && (
                      <DifferenceChip
                        newValue={line.expectedTradePriceInCurrency}
                        origValue={line.originalPriceInCurrency}
                        warningThresholdPct={DISCREPANCY_THRESHOLD_PCT_PRICE}
                        data-testid={`bulk-trader-update-trades-price-difference-${type.toLowerCase()}-${index}`}
                      />
                    )}
                  </TableCell>
                  <NarrowTableCell
                    data-testid={`bulk-trader-update-trades-original-quantity-${type.toLowerCase()}-${index}`}
                  >
                    {line.total !== line.originalTotal && (
                      <Typography variant='bodyLarge' color='GrayText'>
                        {line.originalTotal}
                      </Typography>
                    )}
                  </NarrowTableCell>
                  <TableCell
                    data-testid={`bulk-trader-update-trades-quantity-cell-${type.toLowerCase()}-${index}`}
                  >
                    <TextField
                      value={line.total}
                      sx={{ width: '9em' }}
                      size='small'
                      type='number'
                      data-testid={`bulk-trader-update-trades-quantity-input-${type.toLowerCase()}-${index}`}
                      InputProps={{
                        inputProps: { min: 0 },
                      }}
                      onChange={(e: any) => {
                        bulkTrades[index].total = round(
                          parseFloat(e.target.value),
                          0,
                        );
                        setBulkTrades([...bulkTrades]);
                      }}
                    />
                    {line.total !== line.originalTotal && (
                      <DifferenceChip
                        newValue={line.total}
                        origValue={line.originalTotal}
                        warningThresholdPct={DISCREPANCY_THRESHOLD_PCT_QUANTITY}
                        data-testid={`bulk-trader-update-trades-quantity-difference-${type.toLowerCase()}-${index}`}
                      />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Grid
            container
            sx={{
              justifyContent: 'flex-end',
            }}
          >
            <Grid>
              <Button
                variant='contained'
                sx={{ m: 2 }}
                disabled={updateLoading}
                onClick={() =>
                  updatePriceAndQuantity({
                    variables: {
                      input: {
                        bulkTradeRunId,
                        type,
                        bulkTrades: bulkTrades.map((bulkTrade: any) => ({
                          actualPriceCents: Number(
                            dollars2cents(
                              bulkTrade.expectedTradePriceInCurrency,
                              6,
                            ),
                          ),
                          totalQuantity: bulkTrade.total,
                          financialProductId: bulkTrade.financialProduct.id,
                        })),
                      },
                    },
                  })
                }
                data-testid={`bulk-trader-update-trades-save-${type.toLowerCase()}`}
              >
                {t('save')}
              </Button>
            </Grid>
          </Grid>
        </>
      )}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth='sm'
        fullWidth
        data-testid={`bulk-trader-update-trades-bulk-import-dialog-${type.toLowerCase()}`}
      >
        <DialogTitle
          data-testid={`bulk-trader-update-trades-bulk-import-title-${type.toLowerCase()}`}
        >
          {t('pasteFromExcel')}
        </DialogTitle>
        <DialogContent>
          <Typography
            variant='bodyLarge'
            data-testid={`bulk-trader-update-trades-bulk-import-instructions-${type.toLowerCase()}`}
          >
            {t('orderOfContent')}
          </Typography>
          <TextField
            multiline
            rows={10}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            fullWidth
            data-testid={`bulk-trader-update-trades-bulk-import-textarea-${type.toLowerCase()}`}
          />
          <Button
            variant='contained'
            sx={{ marginTop: 1 }}
            onClick={bulkUpdate}
            data-testid={`bulk-trader-update-trades-bulk-import-update-${type.toLowerCase()}`}
          >
            {t('update')}
          </Button>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default UpdateTradesTable;
